import { useSelector } from 'react-redux'

import { LandingType } from '~/constants/landingConstants'
import SnapshotsAliasTypes from '~/constants/SnapshotsAliasTypes'
import { getWidgetData } from '~/store/duck/landing'
import { getMultipleReminders } from '~/store/duck/reminders'
import type { Order, ReminderProtocol } from '~/types'

export const useAddReminderStatusToItems = <T extends Order | ReminderProtocol>(
  items: T[] | null | undefined,
): (T & { procedureWithReminder: boolean })[] | null | undefined => {
  const remindersIds = useSelector(
    getWidgetData(
      LandingType.CLIENT_AND_PATIENT_SNAPSHOTS,
      SnapshotsAliasTypes.Reminders,
    ),
  )
  const existingReminders = useSelector(
    getMultipleReminders(remindersIds as string[]),
  )

  // Handle null and undefined inputs after hooks
  if (items == null) {
    return items
  }

  return items.map((item) => {
    // For ReminderProtocol, we don't need to check if it's a procedure
    // since they are always related to reminders
    // Explicitly check type: if ReminderProtocol, not a procedure; if Order, check type
    const isProcedure = 'type' in item ? item.type === 'PROCEDURE' : false

    let hasExistingReminder = false

    const isReminderProtocol =
      'reminderName' in item && Boolean(item.reminderName)
    if (existingReminders) {
      // For ReminderProtocol, check if any reminder's protocolId matches the item's id
      if (isReminderProtocol && (item as ReminderProtocol).reminderName) {
        hasExistingReminder = existingReminders.some(
          (reminder) => reminder.protocolId === item.id,
        )
      } else {
        // For an Order - check resolvesWith array
        hasExistingReminder = existingReminders.some((reminder) =>
          reminder.resolvesWith?.some(
            (resolveItem: { id: string }) => resolveItem.id === item.id,
          ),
        )
      }
    }

    // For ReminderProtocol items, procedureWithReminder should be true when a matching reminder exists
    // For Order items, procedureWithReminder should be true when it's a procedure AND has a matching reminder
    const procedureWithReminder = isReminderProtocol
      ? hasExistingReminder
      : isProcedure && hasExistingReminder

    return {
      ...item,
      procedureWithReminder,
    }
  })
}
